import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * useFeatureFlags Hook
 * Fetches and manages feature flags for the current user
 * 
 * Default: All features enabled
 * Fetches from Firestore: users/{uid}/featureFlags
 * 
 * v11.12.29: Initial implementation
 * 
 * @param {Object} user - Firebase auth user object
 * @returns {Object} - Feature flags object with boolean values
 * 
 * Example usage:
 * const featureFlags = useFeatureFlags(user);
 * if (featureFlags.learn) {
 *   // Show Learn tab
 * }
 */
export function useFeatureFlags(user) {
  const [featureFlags, setFeatureFlags] = useState({
    learn: true,
    explain: true,
    activities: true,
    examPrep: true,
    notes: true,
    hubChat: true,
    labs: true
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If no user, return default (all enabled)
    if (!user || !user.uid) {
      console.log('🚫 useFeatureFlags: No user logged in, using defaults (all enabled)');
      setLoading(false);
      return;
    }

    console.log(`🔍 useFeatureFlags: Setting up listener for user ${user.uid}`);

    // Set up real-time listener for feature flags
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnapshot) => {
        if (docSnapshot.exists() && docSnapshot.data().featureFlags) {
          const flags = docSnapshot.data().featureFlags;
          console.log('✅ useFeatureFlags: Loaded feature flags from Firestore:', flags);
          setFeatureFlags({
            learn: flags.learn !== undefined ? flags.learn : true,
            explain: flags.explain !== undefined ? flags.explain : true,
            activities: flags.activities !== undefined ? flags.activities : true,
            examPrep: flags.examPrep !== undefined ? flags.examPrep : true,
            notes: flags.notes !== undefined ? flags.notes : true,
            hubChat: flags.hubChat !== undefined ? flags.hubChat : true,
            labs: flags.labs !== undefined ? flags.labs : true
          });
        } else {
          // No feature flags set, use defaults (all enabled)
          console.log('⚠️ useFeatureFlags: No feature flags in Firestore, using defaults (all enabled)');
          setFeatureFlags({
            learn: true,
            explain: true,
            activities: true,
            examPrep: true,
            notes: true,
            hubChat: true,
            labs: true
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('❌ useFeatureFlags: Error loading feature flags:', error);
        // On error, default to all enabled
        setFeatureFlags({
          learn: true,
          explain: true,
          activities: true,
          examPrep: true,
          notes: true,
          hubChat: true,
          labs: true
        });
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [user]);

  return { featureFlags, loading };
}

export default useFeatureFlags;
